import { itemLocList, ItemLocListParamBody } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CommonListParams, SourceTypeEnum } from "@/types/common";
import { OrderItemLocationDtl, OrderItemV2, OrderV2 } from "@/types/inventoryV2";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import { Button, DatePicker, Dropdown, Flex, Input, notification, Select, Tooltip, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangeEnum, ConfirmedEnum } from "@/types/inventory";
import ExportExcelButton from "@/components/ExportExcelButton";
import { TFunction } from "i18next";
import ExportProgressModal from "@/components/ExportProgressModal";
import { dateFormat } from "@/utils/format";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import { PurchaseOrderItem } from "@/types/purchase";
import PurchaseDetail from "@/pages/Purchase/pages/Order/components/PurchaseDetail";
import OrderDetailV2 from "../Order/components/OrderDetailV2";
import { numFormmater } from "@/pages/Requirement/util";
import { getSourceNumber } from "@/utils/getInventorySourceNo";
import { Requirement } from "@/types/requirement";
import RequirementDetail from "@/pages/Requirement/pages/Detail";
import { SaleOrderItemState } from "@/types/sales";
import ItemsOrderForm from "@/pages/Sales/components/ItemsOrderForm";
import { ItemType } from "antd/lib/menu/interface";
import useInspactionEnum from "@/pages/Warehouse/pages/Inspection/hooks/useInspactionEnum";
import useSourceType from "@/hooks/useSourceType";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import SpecialHandleModal from "./SpecialHandleModal";
import ColSetButton, { ColSetOption } from "@/components/ColSetButton";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";

enum TabEnum {
  IN = '1',
  OUT = '2',
}
interface InventoryDetailListProps {

}

interface InventoryDetailFilters {
  keyword: string;
  locationType: number | undefined;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  arrivalStartTime: Dayjs | null;
  arrivalEndTime: Dayjs | null;
  createdStartAt: Dayjs | null;
  createdEndAt: Dayjs | null;
  orderType: ChangeEnum | undefined;
  confirmed: ConfirmedEnum | undefined;
}

const INITIAL_INVENTORY_DETAIL_FILTERS: InventoryDetailFilters = {
  keyword: '',
  locationType: undefined,
  startTime: null,
  endTime: null,
  arrivalStartTime: null,
  arrivalEndTime: null,
  orderType: undefined,
  confirmed: undefined,
  createdEndAt: null,
  createdStartAt: null
};


const InventoryDetailList: FC<InventoryDetailListProps> = ({ }) => {
  const { t } = useTranslation();
  const { getOpType, getChangeType, changeTypes } = useSourceType();
  const { getSpecialHandling } = useInspactionEnum();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [inventoryType, setInventoryType] = useState<TabEnum>(TabEnum.IN);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [openPur, setOpenPur] = useState<PurchaseOrderItem | null>(null);
  const [openRequirement, setOpenRequirement] = useState<Requirement | null>(null);
  const [openSale, setOpenSale] = useState<SaleOrderItemState | null>(null);
  const [openOrder, setOpenOrder] = useState<OrderV2 | null>(null);
  const [specialItem, setSpecialItem] = useState<OrderItemV2 | null>(null);
  const [visibleCols, setVisibleCols] = useState<ColSetOption[] | null>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<InventoryDetailFilters>(INITIAL_INVENTORY_DETAIL_FILTERS);
  const {
    keyword,
    locationType,
    startTime,
    endTime,
    arrivalStartTime,
    arrivalEndTime,
    createdEndAt,
    createdStartAt,
    orderType,
    confirmed,
  } = draftFilters;

  const [list, total, listLoading, { fetchList, clearData }] = useDataList<OrderItemLocationDtl, [CommonListParams<ItemLocListParamBody>]>({
    fetchApi: itemLocList
  });

  // 关键字按空格拆分后是多个编码则走 productIds，单个则走 keyword
  const getKeywordParams = (kw: string): Pick<ItemLocListParamBody, 'keyword' | 'products'> => {
    const tokens = kw ? kw.trim().split(' ').filter(Boolean) : [];
    if (tokens.length > 1) return { products: tokens };
    if (tokens.length === 1) return { keyword: tokens[0] };
    return {};
  }

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_INVENTORY_DETAIL_FILTERS);
    fetch(0, nextFilters);
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryType, replaceFilters])

  const fetch = (offset: number, filters: InventoryDetailFilters) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize || DEFAULT_PAGE_SIZE;

    let params: CommonListParams<ItemLocListParamBody> = {
      body: {
        locationType: filters?.locationType ?? undefined,
        ...getKeywordParams(filters.keyword),
        confirmState: filters.confirmed,
        orderTypes: filters.orderType !== undefined ? [filters.orderType] : undefined,
        arrivalAtStart: filters.arrivalStartTime?.startOf('day')?.valueOf(),
        arrivalAtEnd: filters.arrivalEndTime?.endOf('day')?.valueOf(),
        createdStartAt: filters.createdStartAt?.startOf('day')?.valueOf(),
        createdEndAt: filters.createdEndAt?.endOf('day')?.valueOf(),
      },
      startTime: filters.startTime?.startOf('day')?.valueOf(),
      endTime: filters.endTime?.endOf('day')?.valueOf(),
      pagination: { offset, take },
    }
    fetchList({
      ...params,
      controller
    })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          clearData();
          notification.error({ message: t('common.serverError') })
        }
      })
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_INVENTORY_DETAIL_FILTERS);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const isColHidden = (key: string) => visibleCols !== null && !visibleCols.some(item => item.key === key);

  const columns: ColumnsType<OrderItemLocationDtl> = useMemo(() => [
    {
      title: t('warehouse.refNo'),
      key: "source",
      hidden: isColHidden('source'),
      width: 180,
      fixed: "left",
      sorter: (a, b) => {
        const valA = getSourceNumber(a.orderItem);
        const valB = getSourceNumber(b.orderItem);
        return valA.localeCompare(valB);
      },
      render(_, record) {
        const item = record.orderItem;
        return <Typography.Text
          ellipsis={{ tooltip: getSourceNumber(item) }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => {
            item?.sourceType === SourceTypeEnum.PURCHASE_ITEM && setOpenPur(item?.source || null)
            item?.sourceType === SourceTypeEnum.REQUIREMENT_ITEM && setOpenRequirement(item?.source?.requirement || null)
            item?.sourceType === SourceTypeEnum.SALE_ITEM && item?.source?.order && setOpenSale({
              ...item.source.order,
              items: [{ ...item.source }]
            })
          }}
        >
          {getSourceNumber(item)}
        </Typography.Text>
      }
    },
    {
      title: t('product.code'),
      key: "productCode",
      hidden: isColHidden('productCode'),
      width: 120,
      sorter: (a, b) => {
        const valA = a?.orderItem?.product?.code ?? "";
        const valB = b?.orderItem?.product?.code ?? "";
        return valA.localeCompare(valB, undefined, { numeric: true });
      },
      render(_, record) {
        return <SText text={record.orderItem?.product?.code ?? ''} />
      }
    },
    {
      title: t('product.name'),
      key: "productName",
      hidden: isColHidden('productName'),
      width: 150,
      render(_, record) {
        return <SText text={record.orderItem?.product?.name ?? ''} />
      }
    },
    {
      title: t('product.brand1'),
      key: "brand1",
      hidden: isColHidden('brand1'),
      width: 120,
      render(_, record) {
        return <SText text={record.orderItem?.product?.model ?? ''} />
      }
    },
    {
      title: t('purchase.brand'),
      key: "brand",
      hidden: isColHidden('brand'),
      width: 120,
      render(_, record) {
        return <SText text={record.orderItem?.ext?.originalBrand ?? ''} />
      }
    },
    {
      title: t('purchase.mpn'),
      key: "mpn",
      hidden: isColHidden('mpn'),
      width: 120,
      render(_, record) {
        return <SText text={record.orderItem?.ext?.originalCode ?? ''} />
      }
    },
    {
      title: t('product.uom'),
      key: "uom",
      hidden: isColHidden('uom'),
      width: 80,
      align: 'center',
      render(_, record) {
        return <SText text={record.orderItem?.product?.uom ?? ''} />
      }
    },
    {
      title: t('warehouse.batchNo'),
      key: "lot",
      hidden: isColHidden('lot'),
      width: 280,
      align: 'center',
      render: (_, record) => <SText text={record.orderItem?.lot || ''} style={{ width: "100%" }} />

    },
    {
      title: t('counting.quantity'),
      key: "count",
      hidden: isColHidden('count'),
      width: 90,
      align: 'center',
      dataIndex: 'count',
      sorter: (a, b) => {
        const valA = a?.count ?? 0;
        const valB = b?.count ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.count ? numFormmater(record.count) : '0'} />
      )
    },
    {
      title: t('warehouse.productOldCount'),
      key: 'productOldCount',
      hidden: isColHidden('productOldCount'),
      width: 90,
      // sorter: (a, b) => {
      //   const valA = a?.productOldCount ?? 0;
      //   const valB = b?.productOldCount ?? 0;
      //   return valA - valB;
      // },
      render: (_, record) => (
        <SText text={record.productOldCount !== null ? numFormmater(record.productOldCount) : ''} />
      )
    },
    {
      title: t('warehouse.productNewCount'),
      key: 'productNewCount',
      hidden: isColHidden('productNewCount'),
      width: 90,
      // sorter: (a, b) => {
      //   const valA = a?.productNewCount ?? 0;
      //   const valB = b?.productNewCount ?? 0;
      //   return valA - valB;
      // },
      render: (_, record) => (
        <SText text={record.productNewCount !== null ? numFormmater(record.productNewCount) : ''} />
      )
    },
    {
      title: t('warehouse.lotItemOldCount'),
      key: 'lotItemOldCount',
      hidden: isColHidden('lotItemOldCount'),
      width: 90,
      // sorter: (a, b) => {
      //   const valA = a?.lotItemOldCount ?? 0;
      //   const valB = b?.lotItemOldCount ?? 0;
      //   return valA - valB;
      // },
      render: (_, record) => (
        <SText text={record.lotItemOldCount !== null ? numFormmater(record.lotItemOldCount) : ''} />
      )
    },
    {
      title: t('warehouse.lotItemNewCount'),
      key: 'lotItemNewCount',
      hidden: isColHidden('lotItemNewCount'),
      width: 90,
      // sorter: (a, b) => {
      //   const valA = a?.lotItemNewCount ?? 0;
      //   const valB = b?.lotItemNewCount ?? 0;
      //   return valA - valB;
      // },
      render: (_, record) => (
        <SText text={record.lotItemNewCount !== null ? numFormmater(record.lotItemNewCount) : ''} />
      )
    },
    {
      title: t('order.operationSource'),
      key: "opType",
      hidden: isColHidden('opType'),
      width: 120,
      dataIndex: "opType",
      render: (_, record) => {
        let info = getOpType(record.orderItem?.order?.opType);
        return (
          info && <SText text={info?.label ?? ""} tagMode color={info?.color} />
        );
      },
    },
    {
      title: t('order.operationStatus'),
      key: "confirmed",
      hidden: isColHidden('confirmed'),
      width: 120,
      dataIndex: "confirmed",
      render: (_, record) => {
        const order = record.orderItem?.order;
        if (!order) return null;
        // 根据tab显示不同的状态文字
        if ([ChangeEnum.IN, ChangeEnum.PRE_IN, ChangeEnum.ORDER_IN, ChangeEnum.TEMP_IN].includes(order.type)) {
          // 入库：0-在途，1-已入库
          const label = order.confirmState === ConfirmedEnum.NOTCONFIRM ? (!!order.arrivalAt ? t('order.signed') : t('order.inTransit')) : t('order.inboundCompleted');
          const color = order.confirmState === ConfirmedEnum.NOTCONFIRM ? 'orange' : 'green';
          return <SText text={label} color={color} tagMode />;
        } else {
          // 出库：0-备料，1-已出库
          const label = order.confirmState === ConfirmedEnum.NOTCONFIRM ? t('order.preparing') : t('order.outboundCompleted');
          const color = order.confirmState === ConfirmedEnum.NOTCONFIRM ? 'orange' : 'green';
          return <SText text={label} color={color} tagMode />;
        }
      },
    },
    {
      title: t('order.oprtType'),
      key: "changeType",
      hidden: isColHidden('changeType'),
      width: 120,
      dataIndex: "changeType",
      render: (_, record) => {
        let info = getChangeType(record.orderItem?.order?.type);
        return (
          info && <SText tagMode text={info?.label ?? ''} color={info?.color} />
        );
      },
    },
    {
      title: t('order.inOutNo'),
      key: "order",
      hidden: isColHidden('order'),
      width: 180,
      fixed: "left",
      sorter: (a, b) => {
        const valA = a?.orderItem?.order?.number ?? "";
        const valB = b?.orderItem?.order?.number ?? "";
        return valA.localeCompare(valB);
      },
      render(_, record) {
        return <Typography.Text
          ellipsis={{ tooltip: record.orderItem?.order?.number ?? '' }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => setOpenOrder(record.orderItem?.order || null)}
        >
          {record.orderItem?.order?.number ?? ''}
        </Typography.Text>
      }
    },
    {
      title: t("product.materialProcess"),
      key: "process",
      hidden: isColHidden('process'),
      width: 120,
      sorter: (a, b) => {
        const valA = a?.orderItem?.product?.process ?? "";
        const valB = b?.orderItem?.product?.process ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => <SText text={record?.orderItem?.product?.process ?? ''} />,
    },
    {
      title: t('order.preparationTime'),
      dataIndex: "createAt",
      key: "createAt",
      hidden: isColHidden('createAt'),
      width: 100,
      render: (_, record) => record?.orderItem?.order?.preTime && dateFormat(dayjs(record.orderItem.order.createdAt)),
    },
    {
      title: t('warehouse.requiredArrivalAt'),
      dataIndex: "preTime",
      key: "preTime",
      hidden: isColHidden('preTime'),
      width: 100,
      render: (_, record) => record?.orderItem?.order?.preTime && dateFormat(dayjs(record.orderItem.order.preTime)),
    },
    {
      title: t('warehouse.actualArrivalAt'),
      dataIndex: "arrivalAt",
      key: "arrivalAt",
      hidden: isColHidden('arrivalAt'),
      width: 100,
      render: (_, record) => record?.orderItem?.order?.arrivalAt && dateFormat(dayjs(record.orderItem.order.arrivalAt)),
    },
    {
      title: t('order.confirmedTime'),
      dataIndex: "confirmedAt",
      key: "confirmedAt",
      hidden: isColHidden('confirmedAt'),
      width: 100,
      render: (_, record) => record?.orderItem?.order?.confirmedAt && dateFormat(dayjs(record.orderItem.order.confirmedAt)),
    },
    {
      title: t('purchase.remark'),
      dataIndex: "desc",
      key: "desc",
      hidden: isColHidden('desc'),
      width: 120,
      render: (_, record) => <SText text={record?.orderItem?.order?.desc ?? ""} />,
    },
    {
      title: t('warehouse.inputer'),
      dataIndex: "creator",
      key: "creator",
      hidden: isColHidden('creator'),
      width: 120,
      render: (_, record) => {
        if (record?.orderItem?.order?.creator) return (
          <Fragment>
            <WMSAvatar
              uniChar={record.orderItem.order.creator.id}
              avatarId={record.orderItem.order.creator.avatar}
              creator={record.orderItem.order.creator}
              alt={record.orderItem.order.creator ? getUserAvatarTip(record.orderItem.order.creator) : ''}
              size={24}
            />
            <span>{getUserName(record.orderItem.order.creator)}</span>
          </Fragment>
        )
        else return '-'
      },
    },
    {
      title: t('warehouse.confirmer'),
      dataIndex: "updater",
      key: "updater",
      hidden: isColHidden('updater'),
      width: 120,
      render: (_, record) => {
        const order = record?.orderItem?.order;
        if (order?.confirmState === ConfirmedEnum.CONFIRM) {
          let user = order?.confirmer || order?.updater || null;
          if (user) return (
            <Fragment>
              <WMSAvatar
                uniChar={user.id}
                avatarId={user.avatar}
                creator={user}
                alt={getUserAvatarTip(user)}
                size={24}
              />
              <span>{getUserName(user)}</span>
            </Fragment>
          )
          else return '-'
        }
        else return '-'
      },
    },
    {
      title: t('inspection.specialHandlingAggregate'),
      key: 'specialHandlingAggregate',
      width: 120,
      fixed: "right",
      hidden: isColHidden('specialHandlingAggregate'),
      render: (_, record) => {
        let info = getSpecialHandling(record.orderItem?.specialHandlingAggregate);
        return info && <SText tagMode text={info.label} color={info.color} />
      },
    },
    {
      title: t('common.opt'),
      key: "opt",
      width: 120,
      fixed: "right",
      hidden: isColHidden('opt'),
      render: (_, record) => (
        <Button type="link" disabled={!record.orderItem} onClick={() => setSpecialItem(record.orderItem)}>{t('inspection.specialHandlingAggregate')}</Button>
      ),
    },
  ], [t, visibleCols]);

  // 可配置列，key 与上方 columns 中各列的 key 一一对应
  const colSetOptions: ColSetOption[] = useMemo(() => [
    { key: 'source', label: t('warehouse.refNo') },
    { key: 'productCode', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'brand1', label: t('product.brand1') },
    { key: 'brand', label: t('purchase.brand') },
    { key: 'mpn', label: t('purchase.mpn') },
    { key: 'uom', label: t('product.uom') },
    { key: 'lot', label: t('warehouse.batchNo') },
    { key: 'count', label: t('counting.quantity') },
    { key: 'productOldCount', label: t('warehouse.productOldCount') },
    { key: 'productNewCount', label: t('warehouse.productNewCount') },
    { key: 'lotItemOldCount', label: t('warehouse.lotItemOldCount') },
    { key: 'lotItemNewCount', label: t('warehouse.lotItemNewCount') },
    { key: 'opType', label: t('order.operationSource') },
    { key: 'confirmed', label: t('order.operationStatus') },
    { key: 'changeType', label: t('order.oprtType') },
    { key: 'order', label: t('order.inOutNo') },
    { key: 'process', label: t('product.materialProcess') },
    { key: 'createAt', label: t('order.preparationTime') },
    { key: 'preTime', label: t('warehouse.requiredArrivalAt') },
    { key: 'arrivalAt', label: t('warehouse.actualArrivalAt') },
    { key: 'confirmedAt', label: t('order.confirmedTime') },
    { key: 'desc', label: t('purchase.remark') },
    { key: 'creator', label: t('warehouse.inputer') },
    { key: 'updater', label: t('warehouse.confirmer') },
    { key: 'specialHandlingAggregate', label: t('inspection.specialHandlingAggregate') },
    { key: 'opt', label: t('common.opt') },
  ], [t]);


  // const tableTabs = [
  //   {
  //     key: TabEnum.IN,
  //     label: t('inventory.inbound'),
  //   },
  //   {
  //     key: TabEnum.OUT,
  //     label: t('inventory.outbound'),
  //   },

  // ];

  const itemColumns = useMemo(() => [
    { key: 'number', label: t('warehouse.refNo') },
    { key: 'code', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'brand1', label: t('product.brand1') },
    { key: 'brand', label: t('purchase.brand') },
    { key: 'mpn', label: t('purchase.mpn') },
    { key: 'uom', label: t('product.uom') },
    { key: 'supplierCode', label: t('purchase.code') },
    { key: 'supplierName', label: t('purchase.name') },
    { key: 'price', label: t('purchase.stPrice') },
    { key: 'lot', label: t('warehouse.batchNo') },
    { key: 'count', label: t('counting.quantity') },
    { key: 'productOldCount', label: t('warehouse.productOldCount') },
    { key: 'productNewCount', label: t('warehouse.productNewCount') },
    { key: 'lotItemOldCount', label: t('warehouse.lotItemOldCount') },
    { key: 'lotItemNewCount', label: t('warehouse.lotItemNewCount') },
    { key: 'specialHandlingAggregate', label: t('inspection.specialHandlingAggregate') },
    { key: 'opType', label: t('order.operationSource') },
    { key: 'confirmed', label: t('order.operationStatus') },
    { key: 'changeType', label: t('order.oprtType') },
    { key: 'order', label: t('order.inOutNo') },
    { key: 'process', label: t('product.materialProcess') },
    { key: 'createAt', label: t('order.preparationTime') },
    { key: 'creator', label: t('warehouse.inputer') },
    { key: 'updater', label: t('warehouse.confirmer') },
    { key: 'preTime', label: t('warehouse.requiredArrivalAt') },
    { key: 'arrivalAt', label: t('warehouse.actualArrivalAt') },
    { key: 'confirmedAt', label: t('order.confirmedTime') },
    { key: 'desc', label: t('purchase.remark') },
    { key: 'finalPurchaseQuantity', label: t('requirement.finalPurQuantity') },
  ], [t]);

  const itemDataMapper = (item: OrderItemLocationDtl, t: TFunction) => {
    const orderItem = item.orderItem;
    const order = orderItem?.order;
    const finalPurchaseQuantity =
      orderItem?.sourceType === SourceTypeEnum.PURCHASE_ITEM
        ? Number(orderItem.source?.count ?? '')
        : orderItem?.sourceType === SourceTypeEnum.REQUIREMENT_ITEM
          ? Number(orderItem.source?.puredCount ?? '')
          : NaN;

    // 确认状态显示不同的状态文字
    let oprtType = getChangeType(order?.type);
    let operationSource = getOpType(order?.opType);
    let inboundStatus = order?.confirmState === ConfirmedEnum.NOTCONFIRM ? (!!order?.arrivalAt ? t('order.signed') : t('order.inTransit')) : t('order.inboundCompleted');
    let outboundStatus = order?.confirmState === ConfirmedEnum.NOTCONFIRM ? t('order.preparing') : t('order.outboundCompleted');
    let isInbound = order ? [ChangeEnum.IN, ChangeEnum.PRE_IN, ChangeEnum.ORDER_IN, ChangeEnum.TEMP_IN].includes(order.type) : false;

    return {
      [t('warehouse.refNo')]: getSourceNumber(orderItem),
      [t('product.code')]: orderItem?.product?.code || '',
      [t('product.name')]: orderItem?.product?.name || '',
      [t('product.brand1')]: orderItem?.product?.model || '',
      [t('purchase.brand')]: orderItem?.ext?.originalBrand ?? '',
      [t('purchase.mpn')]: orderItem?.ext?.originalCode ?? '',
      [t('product.uom')]: orderItem?.product?.uom || '',
      [t('purchase.code')]: orderItem?.sourceType === SourceTypeEnum.PURCHASE_ITEM ? orderItem.source?.order?.supplier?.code ?? '' : '',
      [t('purchase.name')]: orderItem?.sourceType === SourceTypeEnum.PURCHASE_ITEM ? orderItem.source?.order?.supplier?.name ?? '' : '',
      [t('purchase.stPrice')]: orderItem?.sourceType === SourceTypeEnum.PURCHASE_ITEM
        ? numFormmater(Number(orderItem.source?.quotationRequest?.supplierPrice || orderItem.source?.price?.price || ''))
        : '',
      [t('warehouse.batchNo')]: orderItem?.lot || '',
      [t('counting.quantity')]: item.count ? numFormmater(item.count) : '',
      [t('warehouse.productOldCount')]: item.productOldCount !== null ? numFormmater(item.productOldCount) : '',
      [t('warehouse.productNewCount')]: item.productNewCount !== null ? numFormmater(item.productNewCount) : '',
      [t('warehouse.lotItemOldCount')]: item.lotItemOldCount !== null ? numFormmater(item.lotItemOldCount) : '',
      [t('warehouse.lotItemNewCount')]: item.lotItemNewCount !== null ? numFormmater(item.lotItemNewCount) : '',
      [t('inspection.specialHandlingAggregate')]: getSpecialHandling(orderItem?.specialHandlingAggregate)?.label ?? '',
      [t('order.operationSource')]: operationSource?.label ?? '',
      [t('order.operationStatus')]: isInbound ? inboundStatus : outboundStatus,
      [t('order.oprtType')]: oprtType?.label ?? "",
      [t('order.inOutNo')]: order?.number || '',
      [t('product.materialProcess')]: orderItem?.product?.process ?? '',
      [t('order.preparationTime')]: order?.createdAt && dateFormat(dayjs(order.createdAt)),
      [t('warehouse.inputer')]: getUserName(order?.creator) || '',
      [t('warehouse.confirmer')]: order?.confirmState === ConfirmedEnum.CONFIRM ? getUserName(order?.updater || order?.confirmer) : '',
      [t('warehouse.requiredArrivalAt')]: order?.preTime && dateFormat(dayjs(order.preTime)),
      [t('warehouse.actualArrivalAt')]: order?.arrivalAt && dateFormat(dayjs(order.arrivalAt)),
      [t('order.confirmedTime')]: order?.confirmedAt && dateFormat(dayjs(order.confirmedAt)),
      [t('purchase.remark')]: order?.desc ?? "",
      [t('requirement.finalPurQuantity')]:
        Number.isNaN(finalPurchaseQuantity)
          ? ''
          : numFormmater(finalPurchaseQuantity),
    };
  };
  const handleExportAll = async (): Promise<OrderItemLocationDtl[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allOrderData: OrderItemLocationDtl[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        let params: CommonListParams<ItemLocListParamBody> = {
          body: {
            locationType: appliedFilters?.locationType ?? undefined,
            ...getKeywordParams(appliedFilters.keyword),
            confirmState: appliedFilters.confirmed,
            orderTypes: appliedFilters.orderType !== undefined ? [appliedFilters.orderType] : undefined,
            arrivalAtStart: appliedFilters.arrivalStartTime?.startOf('day')?.valueOf(),
            arrivalAtEnd: appliedFilters.arrivalEndTime?.endOf('day')?.valueOf(),
            createdStartAt: appliedFilters.createdStartAt?.startOf('day')?.valueOf(),
            createdEndAt: appliedFilters.createdEndAt?.endOf('day')?.valueOf()
          },
          startTime: appliedFilters.startTime?.startOf('day')?.valueOf(),
          endTime: appliedFilters.endTime?.endOf('day')?.valueOf(),
          pagination: { offset, take: batchSize },
        }
        const response = await itemLocList(params);

        if (response.body && response.body.length > 0) {
          allOrderData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allOrderData.length /
                (response.pagination?.size || allOrderData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);
        } else {
          hasMore = false;
        }

        if ((response?.body?.length || 0) < batchSize) {
          hasMore = false;
        }
      }
      setExportProgress(100);
      return allOrderData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const getExportItems = useCallback(() => {
    let resItems: ItemType[] = [];
    resItems.push(
      {
        key: 1,
        label: (
          <ExportExcelButton
            list={list}
            columns={itemColumns}
            defaultSelectedColumns={[
              t('warehouse.refNo'),
              t('product.code'),
              t('order.oprtType'),
              t('product.brand1'),
              t('warehouse.productNum')
            ]}
            dataMapper={itemDataMapper}
            fileName={`${t('layout.inOutDetail')}.xlsx`}
            type="link"
            style={{ color: "black" }}
          />
        )
      },
      {
        key: 2,
        label: (
          <ExportExcelButton
            isAll
            loadData={handleExportAll}
            columns={itemColumns}
            defaultSelectedColumns={[
              t('warehouse.refNo'),
              t('product.code'),
              t('order.oprtType'),
              t('product.brand1'),
              t('warehouse.productNum')
            ]}
            dataMapper={itemDataMapper}
            fileName={`${t('layout.inOutDetail')}.xlsx`}
            type="link"
            style={{ color: "black" }}
          />
        )
      }
    )
    return resItems;
  }, [t, handleExportAll, itemDataMapper, itemColumns, list])
  const headerChildren = (
    <Flex gap={6} justify='space-between' style={{ width: "100%" }}>
      <Flex gap={6}>
        <ColSetButton
          options={colSetOptions}
          value={visibleCols ?? colSetOptions}
          onOk={setVisibleCols}
        />
        <Dropdown menu={{ items: getExportItems() }}>
          <Button onClick={(e) => e.preventDefault()}>
            {t('common.export')}
          </Button>
        </Dropdown>
      </Flex>
      <Flex align='center' gap={6}>
        <SelectLocationType<number>
          style={{ minWidth: 360 }}
          value={locationType}
          onChange={value => updateDraftFilters({ locationType: value })}
          allowClear
        />
        <Select style={{ minWidth: 180 }} value={confirmed} placeholder={t('warehouse.plconfed')}
          options={[
            { label: t('warehouse.noconf'), value: ConfirmedEnum.NOTCONFIRM },
            { label: t('warehouse.confed'), value: ConfirmedEnum.CONFIRM },
          ]}
          onChange={value => updateDraftFilters({ confirmed: value })} allowClear
        />
        <Select style={{ minWidth: 160 }} value={orderType}
          options={changeTypes}
          onChange={value => updateDraftFilters({ orderType: value })}
          placeholder={t('order.selectChangeTypePl')}
          allowClear
        />
        <Input
          style={{ width: 220 }}
          placeholder={t('warehouse.searchMultiCodePlaceholder')}
          value={keyword}
          onChange={event => updateDraftFilters({ keyword: event.target.value })}
          onPressEnter={handleSearch}
        />
      </Flex>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%", marginBottom: 10 }}>
      <STableLayout
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => { fetch(offset, appliedFilters); }}
        columns={columns as ColumnsType<AnyObject>}
        dataSource={list}
        loading={listLoading}
        scrollX={1200}
        headerChildren={headerChildren}
        headerSub={
          <Flex gap={6} style={{ marginBottom: 8, width: "100%" }} justify='flex-end' >
            <Tooltip title={t('common.createTime')}>
              <DatePicker.RangePicker
                value={[createdStartAt, createdEndAt]}
                onChange={value => updateDraftFilters({
                  createdStartAt: value?.[0] ?? null,
                  createdEndAt: value?.[1] ?? null,
                })}
                style={{ width: 260 }}
              />
            </Tooltip>
            <Tooltip title={t('warehouse.signTime')}>
              <DatePicker.RangePicker
                value={[arrivalStartTime, arrivalEndTime]}
                onChange={value => updateDraftFilters({
                  arrivalStartTime: value?.[0] ?? null,
                  arrivalEndTime: value?.[1] ?? null,
                })}
                placeholder={[t('order.startTime'), t('order.endTime')]}
                style={{ width: 260 }}
              />
            </Tooltip>
            <Tooltip title={t('order.confirmedTime')}>
              <DatePicker.RangePicker
                value={[startTime, endTime]}
                onChange={value => updateDraftFilters({
                  startTime: value?.[0] ?? null,
                  endTime: value?.[1] ?? null,
                })}
                placeholder={[t('order.startTime'), t('order.endTime')]}
                style={{ width: 260 }}
              />
            </Tooltip>
            <Tooltip title={t("purchase.search")}>
              <Button
                type="primary"
                htmlType="button"
                icon={<SearchOutlined />}
                aria-label={t("purchase.search")}
                onClick={handleSearch}
              />
            </Tooltip>
            <Tooltip title={t("purchase.clearFilters")}>
              <Button
                type="primary"
                htmlType="button"
                icon={<DeleteOutlined />}
                aria-label={t("purchase.clearFilters")}
                onClick={handleClearFilters}
              />
            </Tooltip>
          </Flex>
        }
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {specialItem && <SpecialHandleModal open orderItem={specialItem} onClose={() => setSpecialItem(null)} />}
      {
        openPur && <PurchaseDetail
          canEdit={false}
          order={openPur}
          // setRequirementDetail={}
          key={openPur.id}
          handleChangeStatus={() => { }}
          handleOk={() => Promise.resolve()}
          open={!!openPur}
          confirmLoading={false}
          handleCancel={() => setOpenPur(null)}
          orderItemId={openPur.id}
        />
      }
      {
        openRequirement && <RequirementDetail
          id={openRequirement.id}
          handleOk={() => { setOpenRequirement(null) }}
          number={openRequirement.number}
          handleCancel={() => { }}
          onCancel={() => setOpenRequirement(null)}
        />
      }
      {
        openOrder && <OrderDetailV2 readonly
          handleRm={() => { }}
          id={openOrder.id}
          open={true}
          handleCancel={() => setOpenOrder(null)}
        />
      }
      {
        openSale && <ItemsOrderForm editMode="READ" onFormSuccess={() => { }}
          leftTitle={<SText text={t('requirement.saleOrderNumber') + openSale.number} />}
          onClose={() => setOpenSale(null)}
          fetchId={openSale.id}
        />
      }
    </Flex>
  )
};

export default InventoryDetailList;
